/**
 * Text clean-up for the speech-to-text tool: English spoken punctuation, and joining
 * each finished phrase onto the transcript with the right space and capital.
 *
 * The browser's recogniser returns words, not punctuation — say "comma" and you get the
 * word "comma". These rules turn a small, fixed set of spoken commands into marks. They
 * are deliberately literal: "the Victorian period" becomes "the Victorian." too, which
 * is why the widget lets the reader switch them off.
 *
 * Only the whitespace *before* a command is consumed (both sides for line breaks), so
 * the space after a mark is whatever the speaker's next word brought with it, and a
 * number such as 3.5 or a time such as 10:30 is never touched.
 */

interface Command {
  pattern: RegExp;
  mark: string;
}

/** Longest first, so "semicolon" is never read as "semi" + "colon". */
const COMMANDS: readonly Command[] = [
  { pattern: /[ \t]*\bnew paragraph\b[ \t]*/gi, mark: "\n\n" },
  { pattern: /[ \t]*\bnew line\b[ \t]*/gi, mark: "\n" },
  { pattern: /[ \t]*\bexclamation (?:mark|point)\b/gi, mark: "!" },
  { pattern: /[ \t]*\bquestion mark\b/gi, mark: "?" },
  { pattern: /[ \t]*\bfull stop\b/gi, mark: "." },
  { pattern: /[ \t]*\bperiod\b/gi, mark: "." },
  { pattern: /[ \t]*\bsemicolon\b/gi, mark: ";" },
  { pattern: /[ \t]*\bcolon\b/gi, mark: ":" },
  { pattern: /[ \t]*\bcomma\b/gi, mark: "," },
];

/** A lowercase letter that opens a sentence inside the text (not at its very start). */
const SENTENCE_START = /([.?!][ \t]+|\n[ \t]*)(\p{Ll})/gu;
const FIRST_LETTER = /\p{L}/u;
const EDGE_BLANKS = /^[ \t]+|[ \t]+$/g;
const ENDS_SENTENCE = /(?:[.?!]|\n)[ \t]*$/;
const ENDS_WITH_SPACE = /\s$/;
const STARTS_WITH_MARK = /^[.,;:?!\n]/;
/** Scripts written without spaces between words or sentences. */
const SPACELESS_LANGUAGE = /^(?:ja|zh|th|lo|km|my)\b/i;

/** Replace the spoken commands in `text` with punctuation. */
export function applySpokenPunctuation(text: string): string {
  let result = text;
  for (const command of COMMANDS) {
    result = result.replace(command.pattern, command.mark);
  }
  return result.replace(
    SENTENCE_START,
    (_, lead: string, letter: string) => `${lead}${letter.toUpperCase()}`
  );
}

function capitalizeFirst(text: string, locale: string): string {
  const match = FIRST_LETTER.exec(text);
  if (!match) {
    return text;
  }
  const at = match.index;
  const letter = match[0];
  return `${text.slice(0, at)}${letter.toLocaleUpperCase(locale)}${text.slice(at + letter.length)}`;
}

/**
 * Add a finished phrase to the transcript.
 *
 * Capitalised when it starts a sentence (an empty transcript, or one ending in . ? ! or
 * a line break); joined with one space, except after whitespace, before punctuation, or
 * in Japanese, Chinese, Thai and the other languages written without spaces.
 */
export function appendPhrase(
  transcript: string,
  phrase: string,
  language: string
): string {
  const piece = phrase.replace(EDGE_BLANKS, "");
  if (piece === "") {
    return transcript;
  }
  const startsSentence =
    transcript.trim() === "" || ENDS_SENTENCE.test(transcript);
  const text = startsSentence ? capitalizeFirst(piece, language) : piece;
  const noSpace =
    transcript === "" ||
    ENDS_WITH_SPACE.test(transcript) ||
    STARTS_WITH_MARK.test(text) ||
    SPACELESS_LANGUAGE.test(language);
  return `${transcript}${noSpace ? "" : " "}${text}`;
}

const WORD = /[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*/gu;

/** Words in a transcript or script: runs of letters and digits, apostrophes kept inside. */
export function countWords(text: string): number {
  return text.match(WORD)?.length ?? 0;
}
